import { useState } from 'react'
import { Mail, MessageSquare, FileText, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'

export function SupportPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // Open mailto with pre-filled fields
    const subject = encodeURIComponent(`Support Request from ${name}`)
    const body = encodeURIComponent(`From: ${name} (${email})\n\n${message}`)
    window.location.href = `mailto:support@drscribey.com?subject=${subject}&body=${body}`
    setSubmitted(true)
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-brand-500 mb-8 transition-colors">
          <ArrowLeft size={16} />
          Back to Dr. Scribey
        </Link>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Support</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">
          Need help? We're here for you. Reach out and we'll get back to you within 24 hours.
        </p>

        {/* Quick links */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <a
            href="mailto:support@drscribey.com"
            className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg hover:border-brand-400 transition-colors"
          >
            <Mail size={20} className="text-brand-500 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Email Us</div>
              <div className="text-xs text-gray-500">support@drscribey.com</div>
            </div>
          </a>

          <Link
            to="/terms"
            className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg hover:border-brand-400 transition-colors"
          >
            <FileText size={20} className="text-brand-500 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Terms</div>
              <div className="text-xs text-gray-500">Terms of Service</div>
            </div>
          </Link>

          <Link
            to="/privacy"
            className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg hover:border-brand-400 transition-colors"
          >
            <FileText size={20} className="text-brand-500 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Privacy</div>
              <div className="text-xs text-gray-500">Privacy Policy</div>
            </div>
          </Link>
        </div>

        {/* FAQ */}
        <div className="mb-10">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              {
                q: 'Is Dr. Scribey HIPAA compliant?',
                a: 'Yes. All audio and text data is processed securely. We sign Business Associate Agreements (BAAs) with our infrastructure providers and never store raw audio after processing.',
              },
              {
                q: 'How does the free trial work?',
                a: 'You get 10 free encounters to try Dr. Scribey. No credit card required. After that, upgrade to Pro for $99/month for unlimited encounters.',
              },
              {
                q: 'Which EHR systems are supported?',
                a: 'Dr. Scribey works alongside any EHR via copy-paste. The Chrome extension runs in a side panel next to your existing workflow — no integration required.',
              },
              {
                q: 'Can I cancel my subscription?',
                a: 'Yes, anytime. Go to Settings → Manage Subscription in the app. Your access continues until the end of your billing period.',
              },
              {
                q: 'How do I install the Chrome extension?',
                a: 'Install from the Chrome Web Store, sign in with your Dr. Scribey account, and the side panel will be available on any page.',
              },
            ].map((faq, i) => (
              <details key={i} className="group border border-gray-200 dark:border-gray-800 rounded-lg">
                <summary className="flex items-center justify-between p-4 cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-brand-500 transition-colors">
                  {faq.q}
                  <span className="text-gray-400 group-open:rotate-45 transition-transform text-lg">+</span>
                </summary>
                <p className="px-4 pb-4 text-sm text-gray-500 dark:text-gray-400">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* Contact form */}
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare size={20} className="text-brand-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Send a Message</h2>
          </div>

          {submitted ? (
            <div className="text-center py-6">
              <div className="text-3xl mb-2">✉️</div>
              <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">Your email client should have opened.</p>
              <p className="text-xs text-gray-500 mt-1">
                If it didn't, email us directly at{' '}
                <a href="mailto:support@drscribey.com" className="text-brand-500">support@drscribey.com</a>
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-4 text-sm text-brand-500 hover:text-brand-400"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                  required
                />
              </div>
              <button
                type="submit"
                className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Send Message
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-8">
          © 2026 TailwindX, LLC — All rights reserved.
        </p>
      </div>
    </div>
  )
}
